import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getModuleDetails } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from 'react-i18next';
import './ModuleDetail.css';

interface Translation {
  translationId: number;
  languageCode: string;
  name: string;
  title: string;
  description: string;
  createdAt: string;
  updatedAt: string;
}

interface TestItem {
  [key: string]: {
    en: string;
    tr: string;
  };
}

interface Module {
  moduleId: number;
  translationList: Translation[];
  createdAt: string;
  updatedAt: string;
  testList: TestItem[];
}

type RouteParams = {
  moduleId: string;
};

const ModuleDetail: React.FC = () => {
  const [module, setModule] = useState<Module | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const params = useParams<keyof RouteParams>();
  const moduleId = params.moduleId;
  const { language } = useLanguage();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const fetchModuleDetail = async () => {
      try {
        if (!moduleId) {
          setError(t('moduleIdMissing'));
          setLoading(false);
          return;
        }
        
        const data = await getModuleDetails(moduleId);
        // The API returns a single object, not an array
        setModule(data);
        console.log("Module data:", data);
      } catch (err) {
        setError(t('failedToLoadModuleDetails'));
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchModuleDetail();
  }, [moduleId, t]);

  if (loading) {
    return <div className="loading">{t('loadingModuleDetails')}</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  if (!module) {
    return <div className="error">{t('moduleNotFound')}</div>;
  }

  // Find translation based on current language or fall back to English
  const translation = module.translationList.find(
    (tr) => tr.languageCode.toLowerCase() === language.toLowerCase()
  ) || module.translationList.find(
    (tr) => tr.languageCode.toLowerCase() === 'en'
  );

  // Extract tests from the module
  const tests = module.testList || [];

  return (
    <div className="module-detail">
      <header className="module-header">
        <h1>{translation?.name || t('module')}</h1>
        <p>{translation?.description || t('noDescriptionAvailable')}</p>
      </header>

      <div className="tests-container">
        <h2>{t('availableTests')}</h2>
        <div className="tests-grid">
          {tests.map((test) => {
            // Each test is an object with a single key (test ID) and value (translations)
            const testId = Object.keys(test)[0];
            const testTranslations = test[testId];
            // Get the test name in the current language or fall back to English
            const langKey = language.toLowerCase() as keyof typeof testTranslations;
            const testName = (testTranslations && langKey in testTranslations) 
              ? testTranslations[langKey] 
              : testTranslations?.en || t('test');
            
            return (
              <Link 
                to={`/test/${testId}`} 
                key={testId} 
                className="test-card card"
              >
                <div className="test-content">
                  <h3>{testName}</h3>
                  <div className="start-test">
                    <span>{t('startTest')}</span>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M12 5L19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ModuleDetail; 